import React, { useState } from 'react';
import { Modal, Button, Form, Alert, Spinner } from 'react-bootstrap';
import { useParams } from 'react-router-dom';
import { useAgentContext } from '../../context/agent';
import { useMutation, useQueryClient } from 'react-query';
import { addFaq, updateFaq } from '../../api/agent';
import { FaQuestionCircle, FaPlus, FaTimes, FaExclamationTriangle } from 'react-icons/fa';
import { IFAQ } from '../../models/faq';

type Props = {
    show: boolean;
    setShow: React.Dispatch<React.SetStateAction<boolean>>;
	faq?: IFAQ;
	qn?: string;
}

const AddFAQ = ({show, setShow, qn, faq} : Props) => {
    const [question, setQuestion] = useState(qn || "");
    const [answer, setAnswer] = useState("");
    const [error, setError] = useState<string | null>(null);

    const { agentId} = useParams();
    const {getAgentById} = useAgentContext();
    const agent = getAgentById(agentId || "");

    const handleClose = () => setShow(false);

    const queryClient = useQueryClient();
    const { mutateAsync: addFaqFn, isLoading} = useMutation(
        addFaq,
        {
            onSuccess: () => {
                queryClient.invalidateQueries(['Organizations']);
                queryClient.invalidateQueries(['Faqs', agentId]);
                handleClose();
            },
            onError: (error) => {
                setError("Unexpected error. Please try again or contact admin.");
            },
        }
    );
	const { mutateAsync: updateFAQFn, isLoading: isUpdating } = useMutation(
		updateFaq, // mutation function goes here
		{
			onSuccess: () => {
				queryClient.invalidateQueries(['Faqs', agentId]);
				handleClose();
			},
			onError: (error) => {
				setError("Unexpected error. Please try again or contact admin.");
			},
		}
	);

    const handleAdd = async () => {
        if(!question || !answer) {
            setError('Question and Answer required.');
            return
        }
        if(!agent) {
            setError("Unexpected error. Please reload page and retry.")
            return;
        }
        addFaqFn({
            agentId: agent.id,
            orgId: agent.organizationId,
            question,
            answer
        })
    }
	const handleUpdate = async () => {
		if(!question || !answer) {
			setError('Question and Answer required.');
			return
		}

		if(!agent || !faq) {
            setError("Unexpected error. Please reload page and retry.")
            return;
        }
		
		updateFAQFn({
			orgId: agent.organizationId,
			agentId: agent.id,
			id: faq.id,
			question,
			answer,
		})
	}

    return (
        <Modal 
            show={show} 
            onHide={handleClose}
            size="lg"
            contentClassName="bg-dark border-secondary"
        >
            <Modal.Header 
                closeButton
                className="border-secondary"
                style={{ backgroundColor: "#2d2d2d" }}
            >
                <div className="d-flex align-items-center">
                    <div 
                        className="me-3 d-flex align-items-center justify-content-center"
                        style={{
                            width: "32px",
                            height: "32px",
                            backgroundColor: "#ffc107",
                            borderRadius: "8px"
                        }}
                    >
                        <FaQuestionCircle className="text-white" size={16} />
                    </div>
                    <Modal.Title className="text-light mb-0" style={{ fontSize: "18px", fontWeight: "600" }}>
                        Add FAQ
                    </Modal.Title>
                </div>
            </Modal.Header>
            
            <Modal.Body style={{ backgroundColor: "#1a1a1a" }}>
                <Form>
                    <Form.Group className="mb-4">
                        <Form.Label className="text-light fw-semibold mb-2">
                            Question
                        </Form.Label>
                        <Form.Text className="d-block mb-3" style={{ color: "#ccc" }}>
                            Enter the frequently asked question that customers might have.
                        </Form.Text>
                        <Form.Control
                            id='question'
                            type='text'
                            value={question}
                            onChange={(e) => setQuestion(e.target.value)}
                            className="bg-dark text-light border-secondary mb-4"
                            style={{ 
                                backgroundColor: "#2d2d2d",
                                borderColor: "#555",
                                color: "#fff"
                            }}
                            placeholder="What is your return policy?"
                        />
                        
                        <Form.Label className="text-light fw-semibold mb-2">
                            Answer
                        </Form.Label>
                        <Form.Text className="d-block mb-3" style={{ color: "#ccc" }}>
                            Provide a clear and helpful answer to this question.
                        </Form.Text>
                        <Form.Control
                            id='answer'
                            as="textarea" 
                            rows={6}
                            value={answer}
                            onChange={(e) => setAnswer(e.target.value)}
                            className="bg-dark text-light border-secondary"
                            style={{ 
                                backgroundColor: "#2d2d2d",
                                borderColor: "#555",
                                color: "#fff",
                                resize: "vertical"
                            }}
                            placeholder="We offer a 30-day return policy for all unused items..."
                        />
                    </Form.Group>
                </Form>
                
                {error && (
                    <Alert 
                        variant="danger" 
                        className="mt-3"
                        style={{ 
                            backgroundColor: "#2d1a1a",
                            borderColor: "#dc3545",
                            color: "#ff6b6b"
                        }}
                    >
                        <div className="d-flex align-items-center">
                            <FaExclamationTriangle className="me-2" />
                            {error}
                        </div>
                    </Alert>
                )}
            </Modal.Body>
            
            <Modal.Footer 
                className="border-secondary"
                style={{ backgroundColor: "#2d2d2d" }}
            >
                <Button 
                    variant="outline-secondary" 
                    onClick={handleClose}
                    className="d-flex align-items-center"
                    style={{ borderColor: "#555", color: "#ccc" }}
                >
                    <FaTimes className="me-2" size={14} />
                    Cancel
                </Button>
                <Button 
                    variant="warning" 
                    onClick={handleAdd}
                    disabled={isLoading}
                    className="d-flex align-items-center"
                    style={{ 
                        backgroundColor: "#ffc107",
                        borderColor: "#ffc107"
                    }}
                >
                    {isLoading ? (
                        <>
                            <Spinner size="sm" className="me-2" />
                            Adding...
                        </>
                    ) : (
                        <>
                            <FaPlus className="me-2" size={14} />
                            Add FAQ
                        </>
                    )}
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

export default AddFAQ;